import { useState, useEffect, useCallback } from 'react';
import { communityApi } from './communityApi';
import { Heart, MessageCircle, Trash2, Send, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MiniAvatar = ({ username }) => {
  const colors = ['from-emerald-500/40 to-emerald-900/40', 'from-blue-500/40 to-blue-900/40', 'from-purple-500/40 to-purple-900/40', 'from-amber-500/40 to-amber-900/40', 'from-red-500/40 to-red-900/40', 'from-cyan-500/40 to-cyan-900/40'];
  const idx = (username || '').charCodeAt(0) % colors.length;
  return (
    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${colors[idx]} flex items-center justify-center font-display font-bold text-white text-[10px] border border-white/10 flex-shrink-0`}>
      {(username || '?')[0]}
    </div>
  );
};

const timeSince = (dateStr) => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days}j`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const CommentItem = ({ comment, replies, onReply, onDelete, onLike, depth = 0 }) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await onReply(comment.id, replyText.trim());
      setReplyText('');
      setShowReplyInput(false);
    } catch { toast.error('Erreur lors de l\'envoi'); }
    finally { setSending(false); }
  };

  return (
    <div className={`${depth > 0 ? 'ml-8 border-l border-white/5 pl-4' : ''}`} data-testid={`comment-${comment.id}`}>
      <div className="flex gap-2.5 py-2.5">
        <MiniAvatar username={comment.author} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-white text-xs font-semibold">{comment.author}</span>
            {comment.is_owner && <span className="text-[8px] px-1 py-0.5 rounded bg-[#00e5a0]/10 text-[#00e5a0] font-bold">VOUS</span>}
            <span className="text-cap-text-muted text-[10px]">{timeSince(comment.created_at)}</span>
          </div>
          <p className="text-cap-text-secondary text-sm leading-relaxed">{comment.content}</p>
          {/* Actions */}
          <div className="flex items-center gap-4 mt-1.5">
            <button onClick={() => onLike(comment.id)}
              className={`flex items-center gap-1 text-[11px] transition-colors ${comment.user_liked ? 'text-red-400' : 'text-cap-text-muted hover:text-red-400'}`}
              data-testid={`comment-like-${comment.id}`}>
              <Heart className={`w-3 h-3 ${comment.user_liked ? 'fill-red-400' : ''}`} />
              {comment.likes_count > 0 && <span>{comment.likes_count}</span>}
            </button>
            {depth === 0 && (
              <button onClick={() => setShowReplyInput(!showReplyInput)}
                className="flex items-center gap-1 text-[11px] text-cap-text-muted hover:text-[#4f8eff] transition-colors"
                data-testid={`reply-btn-${comment.id}`}>
                <MessageCircle className="w-3 h-3" /> Répondre
              </button>
            )}
            {comment.is_owner && (
              <button onClick={() => onDelete(comment.id)}
                className="flex items-center gap-1 text-[11px] text-cap-text-muted hover:text-red-400 transition-colors"
                data-testid={`comment-delete-${comment.id}`}>
                <Trash2 className="w-3 h-3" /> Supprimer
              </button>
            )}
          </div>
          {/* Reply input */}
          {showReplyInput && (
            <div className="flex items-center gap-2 mt-2" data-testid={`reply-input-${comment.id}`}>
              <input
                className="flex-1 bg-[#0d0f14] border border-white/10 rounded-lg px-3 h-8 text-white text-xs outline-none placeholder:text-cap-text-muted focus:border-[#00e5a0]/40"
                placeholder="Écrire une réponse..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleReply()}
                autoFocus
              />
              <button onClick={handleReply} disabled={!replyText.trim() || sending}
                className="h-8 w-8 rounded-lg bg-[#00e5a0] flex items-center justify-center text-[#0d0f14] disabled:opacity-40">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Replies */}
      {replies && replies.length > 0 && (
        <div>
          {replies.map(r => (
            <CommentItem key={r.id} comment={r} replies={[]} onReply={onReply} onDelete={onDelete} onLike={onLike} depth={1} />
          ))}
        </div>
      )}
    </div>
  );
};

const ThesisComments = ({ thesisId, commentsCount, onCountChange }) => {
  const [comments, setComments] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await communityApi.getComments(thesisId);
      setComments(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [thesisId]);

  useEffect(() => {
    if (expanded) loadComments();
  }, [expanded, loadComments]);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    setSending(true);
    try {
      const c = await communityApi.addComment(thesisId, newComment.trim());
      setComments(prev => [...prev, c]);
      setNewComment('');
      onCountChange?.(1);
      toast.success('+10 XP — Commentaire ajouté !');
    } catch { toast.error('Erreur lors de l\'envoi'); }
    finally { setSending(false); }
  };

  const handleReply = async (parentId, content) => {
    const c = await communityApi.addComment(thesisId, content, parentId);
    setComments(prev => [...prev, c]);
    onCountChange?.(1);
    toast.success('+10 XP — Réponse ajoutée !');
  };

  const handleDelete = async (commentId) => {
    try {
      const comment = comments.find(c => c.id === commentId);
      const replyCount = comments.filter(c => c.parent_id === commentId).length;
      await communityApi.deleteComment(commentId);
      setComments(prev => prev.filter(c => c.id !== commentId && c.parent_id !== commentId));
      onCountChange?.(-(1 + replyCount));
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleLike = async (commentId) => {
    try {
      const result = await communityApi.toggleCommentLike(commentId);
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, likes_count: result.likes_count, user_liked: result.liked } : c
      ));
    } catch { /* ignore */ }
  };

  // Build threaded structure
  const topLevel = comments.filter(c => !c.parent_id);
  const repliesMap = {};
  comments.filter(c => c.parent_id).forEach(c => {
    if (!repliesMap[c.parent_id]) repliesMap[c.parent_id] = [];
    repliesMap[c.parent_id].push(c);
  });

  return (
    <div>
      {/* Toggle button */}
      <button onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-cap-text-secondary hover:text-[#4f8eff] transition-colors text-xs"
        data-testid={`toggle-comments-${thesisId}`}>
        <MessageCircle className="w-4 h-4" />
        <span>{commentsCount}</span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {/* Comments section */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-white/5" data-testid={`comments-section-${thesisId}`}>
          {loading ? (
            <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-cap-text-muted" /></div>
          ) : (
            <>
              {topLevel.length === 0 && (
                <p className="text-cap-text-muted text-xs text-center py-3">Aucun commentaire. Soyez le premier !</p>
              )}
              {topLevel.map(c => (
                <CommentItem key={c.id} comment={c} replies={repliesMap[c.id] || []}
                  onReply={handleReply} onDelete={handleDelete} onLike={handleLike} />
              ))}
            </>
          )}
          {/* New comment input */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-white/5" data-testid={`new-comment-${thesisId}`}>
            <input
              className="flex-1 bg-[#0d0f14] border border-white/10 rounded-lg px-3 h-9 text-white text-sm outline-none placeholder:text-cap-text-muted focus:border-[#00e5a0]/40"
              placeholder="Écrire un commentaire..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
            <button onClick={handleSubmit} disabled={!newComment.trim() || sending}
              className="h-9 px-3 rounded-lg bg-[#00e5a0] text-[#0d0f14] text-xs font-semibold disabled:opacity-40 flex items-center gap-1"
              data-testid={`submit-comment-${thesisId}`}>
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThesisComments;
